/* MUI */
import { Box, Typography } from '@mui/material';

/* Shared */
import { FontSizes } from 'application/Shared/Constants';
import { Helpers } from 'application/Shared/Helpers';

/* React */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataVisualCard } from './DataVisualCard';

const format = (number: number) => {
    // Nine zeros for Billions
    if (Number(number) >= 1.0e9) {
        return `${(number / 1.0e9).toFixed(2)}B`;
    }

    // Six zeros for Millions
    if (Number(number) >= 1.0e6) {
        return `${(number / 1.0e6).toFixed(2)}MM`;
    }

    // Three zeros for Thousands
    if (Number(number) >= 1.0e3) {
        return `${(number / 1.0e3).toFixed(2)}K`;
    }

    return `${number}`;
};

interface DataAPIVisualsTotalProps {
    card?: boolean;
    data: unknown[];
    filteredResults?: boolean;
    icon?: React.ReactNode;
    loading?: boolean;
    noDataMessage?: string;
    subtitle?: string;
    title: string;
    tooltip?: string;
}

export const DataAPIVisualsTotal: React.FC<DataAPIVisualsTotalProps> = ({
    card,
    data,
    filteredResults,
    icon,
    loading,
    noDataMessage,
    subtitle,
    title,
    tooltip
}: DataAPIVisualsTotalProps) => {
    const { t } = useTranslation();
    const [total, setTotal] = useState<string | null>(null);

    useEffect(() => {
        const totalTmp = data?.length;
        if (Helpers.isNum(totalTmp) && totalTmp != null) {
            const formattedTotal = format(totalTmp);
            setTotal(formattedTotal);
        }
    }, [data]);

    if (card) {
        return (
            <DataVisualCard
                filteredResults={filteredResults}
                icon={icon}
                loading={loading || total === null}
                subtitle={subtitle}
                title={title}
                tooltip={tooltip}
            >
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                    width="100%"
                    style={{
                        minHeight: '120px', // 120px accounts for the marginBottom: 8px of h1
                        textAlign: 'center'
                    }}
                    data-testid="dataAPIVisualsTotalBox"
                    data-trackid="dataAPIVisualsTotalBox"
                >
                    {total != null ? (
                        <Typography color="text.cardKPI" variant="h1">
                            {total}
                        </Typography>
                    ) : (
                        <Typography
                            color="text.cardKPINoData"
                            variant="body2"
                            sx={{ fontSize: FontSizes[2] }}
                        >
                            {noDataMessage || t('Common.noData')}
                        </Typography>
                    )}
                </Box>
            </DataVisualCard>
        );
    }
    return total;
};
